/**
 * ContactsManager - Manage customers and vendors
 * Prerequisite component for the invoicing add-on.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Contact } from '../../../types/accounting';
import { accountingApi } from '../../../services/accountingApi';

interface ContactsManagerProps {
  businessId: number;
  /** Country of the business; 1099 reporting only applies in the United States */
  country?: string;
}

type FilterTab = 'all' | 'customer' | 'vendor';

const PAYMENT_TERMS_LABELS: Record<string, string> = {
  'due_on_receipt': 'Due on Receipt',
  'net_15': 'Net 15',
  'net_30': 'Net 30',
  'net_45': 'Net 45',
  'net_60': 'Net 60',
  'net_90': 'Net 90'
};

const EMPTY_FORM = {
  contact_type: 'customer' as 'customer' | 'vendor' | 'both',
  company_name: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  province_state: '',
  postal_zip: '',
  country: 'Canada',
  tax_number: '',
  payment_terms: 'net_30',
  notes: '',
  is_1099_eligible: false,
  default_account_id: null as number | null
};

const ContactsManager: React.FC<ContactsManagerProps> = ({ businessId, country }) => {
  const isUnitedStates = ['usa', 'united states', 'us'].includes((country || '').toLowerCase());
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingContactId, setEditingContactId] = useState<number | null>(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await accountingApi.getContacts(businessId);
      if (response.status === 'success' && response.data) {
        setContacts(response.data.contacts);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Auto-clear success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const filteredContacts = contacts.filter(contact => {
    // Filter by tab
    if (filterTab === 'customer' && contact.contact_type !== 'customer' && contact.contact_type !== 'both') {
      return false;
    }
    if (filterTab === 'vendor' && contact.contact_type !== 'vendor' && contact.contact_type !== 'both') {
      return false;
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const searchFields = [
        contact.display_name,
        contact.email,
        contact.phone,
        contact.company_name,
        contact.first_name,
        contact.last_name
      ];
      return searchFields.some(field => field && field.toLowerCase().includes(query));
    }

    return true;
  });

  const handleAddNew = () => {
    setFormData({ ...EMPTY_FORM });
    setEditingContactId(null);
    setShowForm(true);
    setError(null);
  };

  const handleEdit = (contact: Contact) => {
    setFormData({
      contact_type: contact.contact_type,
      company_name: contact.company_name || '',
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      address_line1: contact.address_line1 || '',
      address_line2: contact.address_line2 || '',
      city: contact.city || '',
      province_state: contact.province_state || '',
      postal_zip: contact.postal_zip || '',
      country: contact.country || 'Canada',
      tax_number: contact.tax_number || '',
      payment_terms: contact.payment_terms || 'net_30',
      notes: contact.notes || '',
      is_1099_eligible: contact.is_1099_eligible,
      default_account_id: contact.default_account_id
    });
    setEditingContactId(contact.id);
    setShowForm(true);
    setError(null);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingContactId(null);
    setFormData({ ...EMPTY_FORM });
    setError(null);
  };

  const handleSave = async () => {
    // Validate
    const hasCompany = formData.company_name.trim().length > 0;
    const hasName = formData.first_name.trim().length > 0 || formData.last_name.trim().length > 0;
    if (!hasCompany && !hasName) {
      setError('Company name or contact name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (editingContactId) {
        const response = await accountingApi.updateContact(editingContactId, formData);
        if (response.status === 'success') {
          setSuccessMessage('Contact updated');
          await loadContacts();
          handleCancel();
        }
      } else {
        const response = await accountingApi.createContact(businessId, formData);
        if (response.status === 'success') {
          setSuccessMessage('Contact created');
          await loadContacts();
          handleCancel();
        }
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to save contact';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (contact: Contact) => {
    if (!window.confirm('Deactivate ' + contact.display_name + '? They will be hidden from lists but data is preserved.')) {
      return;
    }

    try {
      const response = await accountingApi.deleteContact(contact.id);
      if (response.status === 'success') {
        setSuccessMessage('Contact deactivated');
        await loadContacts();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate contact');
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Styles
  const sectionStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    padding: '1.5rem'
  };

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '0.5rem 1rem',
    border: 'none',
    borderBottom: isActive ? '2px solid #059669' : '2px solid transparent',
    background: 'none',
    color: isActive ? '#059669' : '#6b7280',
    fontWeight: isActive ? 600 : 400,
    cursor: 'pointer',
    fontSize: '0.9rem'
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.9rem'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontWeight: 500,
    marginBottom: '0.25rem',
    fontSize: '0.85rem',
    color: '#374151'
  };

  const typeBadge = (type: string): React.CSSProperties => ({
    padding: '0.15rem 0.5rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 500,
    backgroundColor: type === 'customer' ? '#d1fae5' : type === 'vendor' ? '#dbeafe' : '#fef3c7',
    color: type === 'customer' ? '#065f46' : type === 'vendor' ? '#1e40af' : '#92400e'
  });

  if (loading && contacts.length === 0) {
    return (
      <section style={sectionStyle}>
        <h3 style={{ margin: '0 0 1rem' }}>Contacts</h3>
        <p style={{ color: '#6b7280' }}>Loading contacts...</p>
      </section>
    );
  }

  return (
    <section>
      <div style={{ ...sectionStyle, marginBottom: '1rem' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Customers & Vendors</h3>
          <button
            onClick={handleAddNew}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            + Add Contact
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div style={{ padding: '0.75rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', marginBottom: '1rem' }}>
            {error}
          </div>
        )}
        {successMessage && (
          <div style={{ padding: '0.75rem', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', color: '#059669', marginBottom: '1rem' }}>
            {successMessage}
          </div>
        )}

        {/* Inline form */}
        {showForm && (
          <div style={{ border: '1px solid #d1d5db', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem', backgroundColor: '#fafafa' }}>
            <h4 style={{ margin: '0 0 1rem', color: '#111827' }}>
              {editingContactId ? 'Edit Contact' : 'New Contact'}
            </h4>

            {/* Row 1: Type + Company */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Type *</label>
                <select
                  value={formData.contact_type}
                  onChange={e => updateField('contact_type', e.target.value)}
                  style={inputStyle}
                >
                  <option value="customer">Customer</option>
                  <option value="vendor">Vendor</option>
                  <option value="both">Both</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Company Name</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={e => updateField('company_name', e.target.value)}
                  placeholder="e.g. Acme Corp"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Row 2: First/Last Name */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>First Name</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={e => updateField('first_name', e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Last Name</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={e => updateField('last_name', e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Row 3: Email + Phone */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => updateField('email', e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => updateField('phone', e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Row 4: Address */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Address Line 1</label>
              <input
                type="text"
                value={formData.address_line1}
                onChange={e => updateField('address_line1', e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Address Line 2</label>
              <input
                type="text"
                value={formData.address_line2}
                onChange={e => updateField('address_line2', e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* Row 5: City, Province, Postal, Country */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={e => updateField('city', e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Province/State</label>
                <input
                  type="text"
                  value={formData.province_state}
                  onChange={e => updateField('province_state', e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Postal/ZIP</label>
                <input
                  type="text"
                  value={formData.postal_zip}
                  onChange={e => updateField('postal_zip', e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Country</label>
                <select
                  value={formData.country}
                  onChange={e => updateField('country', e.target.value)}
                  style={inputStyle}
                >
                  <option value="Canada">Canada</option>
                  <option value="USA">USA</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Row 6: Tax Number + Payment Terms */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Tax Number (HST/GST/EIN)</label>
                <input
                  type="text"
                  value={formData.tax_number}
                  onChange={e => updateField('tax_number', e.target.value)}
                  placeholder="e.g. 123456789RT0001"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Payment Terms</label>
                <select
                  value={formData.payment_terms}
                  onChange={e => updateField('payment_terms', e.target.value)}
                  style={inputStyle}
                >
                  {Object.entries(PAYMENT_TERMS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 7: 1099 Eligible checkbox (US vendors only) */}
            {isUnitedStates && (formData.contact_type === 'vendor' || formData.contact_type === 'both') && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_1099_eligible}
                    onChange={e => updateField('is_1099_eligible', e.target.checked)}
                  />
                  <span style={{ fontSize: '0.85rem', color: '#374151' }}>1099 Eligible (US vendors)</span>
                </label>
              </div>
            )}

            {/* Row 8: Notes */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Notes</label>
              <textarea
                value={formData.notes}
                onChange={e => updateField('notes', e.target.value)}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Internal notes about this contact..."
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  opacity: saving ? 0.7 : 1
                }}
              >
                {saving ? 'Saving...' : (editingContactId ? 'Update Contact' : 'Create Contact')}
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Filter tabs + Search */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex' }}>
            <button style={tabStyle(filterTab === 'all')} onClick={() => setFilterTab('all')}>
              All ({contacts.length})
            </button>
            <button style={tabStyle(filterTab === 'customer')} onClick={() => setFilterTab('customer')}>
              Customers ({contacts.filter(c => c.contact_type === 'customer' || c.contact_type === 'both').length})
            </button>
            <button style={tabStyle(filterTab === 'vendor')} onClick={() => setFilterTab('vendor')}>
              Vendors ({contacts.filter(c => c.contact_type === 'vendor' || c.contact_type === 'both').length})
            </button>
          </div>
          <div style={{ paddingBottom: '0.5rem' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search contacts..."
              style={{
                padding: '0.4rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.85rem',
                minWidth: '200px'
              }}
            />
          </div>
        </div>

        {/* Contacts table */}
        {filteredContacts.length === 0 ? (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem 0' }}>
            {contacts.length === 0
              ? 'No contacts yet. Click "+ Add Contact" to get started.'
              : 'No contacts match your search.'}
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', fontWeight: 600, color: '#374151' }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', fontWeight: 600, color: '#374151' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', fontWeight: 600, color: '#374151' }}>Email</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', fontWeight: 600, color: '#374151' }}>Phone</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', fontWeight: 600, color: '#374151' }}>Terms</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 0.5rem', fontWeight: 600, color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map(contact => (
                  <tr
                    key={contact.id}
                    style={{ borderBottom: '1px solid #f3f4f6' }}
                  >
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <div style={{ fontWeight: 500 }}>{contact.display_name}</div>
                      {contact.company_name && contact.first_name && (
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          {contact.first_name} {contact.last_name}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <span style={typeBadge(contact.contact_type)}>{contact.contact_type}</span>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#6b7280' }}>
                      {contact.email || '-'}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#6b7280' }}>
                      {contact.phone || '-'}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#6b7280' }}>
                      {PAYMENT_TERMS_LABELS[contact.payment_terms] || contact.payment_terms}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>
                      <button
                        onClick={() => handleEdit(contact)}
                        style={{
                          padding: '0.25rem 0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          marginRight: '0.5rem'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeactivate(contact)}
                        style={{
                          padding: '0.25rem 0.75rem',
                          border: '1px solid #fecaca',
                          borderRadius: '4px',
                          backgroundColor: '#fef2f2',
                          color: '#dc2626',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        Deactivate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default ContactsManager;
